import {useCallback, useContext} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import {metrics} from '~/vendor/metrics';
import projectClient from '~/vendor/projectService';
import rollbar from '~/vendor/rollbar';
// actions
import * as NotificationActions from '~/actions/NotificationActions';
import * as AnalyticsActions from '~/actions/AnalyticsActions';
import * as PathActions from '~/slices/pathSlice';
import {setRestingScreenState} from '~/actions/WelcomeActions';
import {loadComplements} from '~/actions/ComplementsActions';
import {selectIcon} from '~/actions/SelectionActions';
import * as CanvasActions from '~/actions/CanvasActions';
// constants
import * as NotificationConstants from '~/constants/NotificationConstants';
import remoteServices from '~/constants/RemoteServices';
import {
  MAXIMUM_PRODUCT_COUNT as FUNDRAISING_MAXIMUM_PRODUCT_COUNT,
  NAMES_NUMBERS_ERROR_MESSAGE
} from '~/constants/FundraisingConstants';
import {
  FEEDBACK_MAILTO_ADDRESS,
  SUPPORT_MAILTO_ADDRESS,
  SUPPORT_PHONE_NUMBER
} from '~/constants/ContactConstants';
import {
  DOOR_OPENER_CATEGORY_ID,
  EXEMPT_BLANK_MASK_IDS,
  MASKS_CATEGORY_ID,
  SLEEVE_COLOR_LIMIT
} from '~/constants/ProductConstants';
import {DESIGN_IDEAS_EMAIL} from '~/constants/EmailConstants';
// selectors
import {
  hasQuote as hasQuoteSelector,
  hasTouchedQuantities as hasTouchedQuantitiesSelector
} from '~/selectors/QuoteSelectors';
import * as CanvasSelectors from '~/selectors/CanvasSelectors';
import * as ProductSelectors from '~/selectors/ProductSelectors';
import * as DesignSelectors from '~/selectors/DesignSelectors';
import {LocationContext} from '~/components/LocationInfo';
// utils
import {triggerPageViewAnalyticsTag} from '~/utils/analytics';

export const routeHandlersPropTypes = PropTypes.shape({
  addItemsColorHandler: PropTypes.func,
  addItemsColorSelectedHandler: PropTypes.func,
  addItemsIndexHandler: PropTypes.func,
  addItemsProductHandler: PropTypes.func,
  artToolsHandler: PropTypes.func,
  buyHandler: PropTypes.func,
  changeProductHandler: PropTypes.func,
  checkoutHandler: PropTypes.func,
  emailConfirmationHandler: PropTypes.func,
  gofHandler: PropTypes.func,
  intlQuoteHandler: PropTypes.func,
  nextHandler: PropTypes.func,
  quoteHandler: PropTypes.func,
  saveContinueHandler: PropTypes.func,
  saveContinueLeaveHandler: PropTypes.func,
  selectIncompatibleProductHandler: PropTypes.func,
  shareEmailHandler: PropTypes.func,
  summaryHandler: PropTypes.func,
  tipsHandler: PropTypes.func,
  verifyHandler: PropTypes.func,
  verifyIntlHandler: PropTypes.func,
  onLeaveAddItemsColorHandler: PropTypes.func
});

const useRouteHandlers = () => {
  const hasQuote = useSelector(hasQuoteSelector);
  const hasMultiItem = useSelector(ProductSelectors.hasMultiItem);
  const anchorStyleId = useSelector(ProductSelectors.getAnchorStyleId);
  const hasTouchedQuantities = useSelector(hasTouchedQuantitiesSelector);
  const blankDesign = useSelector(CanvasSelectors.isBlank);
  const namesIcon = useSelector(CanvasSelectors.namesIconSelector);
  const numbersIcon = useSelector(CanvasSelectors.numbersIconSelector);
  const anchorStyle = useSelector(ProductSelectors.getAnchorStyle);
  const style = useSelector(ProductSelectors.getAnchorStyle);
  const tooManyColors = useSelector(ProductSelectors.isAnchorOverColorLimit);
  const tooManySleeveColors = useSelector(
    ProductSelectors.isAnchorOverSleeveColorLimit
  );
  const hasNamesAndNumbers = useSelector(CanvasSelectors.hasNamesAndNumbers);
  const productAllowsNamesAndNumbers = useSelector(
    ProductSelectors.allowsNamesAndNumbers
  );
  const productSelections = useSelector(ProductSelectors.getSelections);
  const compositeId = useSelector(DesignSelectors.getCompositeId);
  const {email} = useSelector(DesignSelectors.getState);
  const canvasDirty = useSelector(CanvasSelectors.isDirty);
  const productDirty = useSelector(ProductSelectors.isDirty);
  const iconsArray = useSelector(CanvasSelectors.getIconsArray);
  const isAnchorComplementsLoaded = useSelector(
    ProductSelectors.isAnchorComplementsLoaded
  );
  const isAtProductLimit = useSelector(ProductSelectors.isAtProductLimit);
  const selectedViewId = useSelector(CanvasSelectors.selectedViewId);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationContext = useContext(LocationContext);
  const [searchParams] = useSearchParams(window.location.search);
  const queryParams = Object.fromEntries([...searchParams]);
  const lastPath = locationContext.locationInfo?.prevLocation?.pathname;

  const checkBlankDesign = useCallback(
    lastPath => {
      if (blankDesign && anchorStyle) {
        const {primary_category_id: categoryId, id} = anchorStyle;

        if (
          (categoryId === MASKS_CATEGORY_ID ||
            categoryId === DOOR_OPENER_CATEGORY_ID) &&
          !EXEMPT_BLANK_MASK_IDS.includes(parseInt(id, 10))
        ) {
          navigate(lastPath);
          dispatch(
            NotificationActions.error({
              key: NotificationConstants.BUY_BLANK_FROM_STORE
            })
          );

          return false;
        }

        if (!anchorStyle.allow_blank) {
          navigate(lastPath);
          dispatch(
            NotificationActions.error({
              key: NotificationConstants.QUOTE_WITHOUT_DESIGN
            })
          );

          return false;
        }
      }

      return true;
    },
    [anchorStyle, blankDesign, dispatch, navigate]
  );

  const checkColorCount = useCallback(
    (lastPath, checkSleeveOnly = false) => {
      if (checkSleeveOnly) {
        if (tooManySleeveColors) {
          navigate(lastPath);
          dispatch(
            NotificationActions.error({
              key: NotificationConstants.TOO_MANY_SLEEVE_COLORS,
              colorLimit: SLEEVE_COLOR_LIMIT
            })
          );
        }
        return !tooManySleeveColors;
      } else {
        if (tooManyColors) {
          navigate(lastPath);
          dispatch(
            NotificationActions.error({
              key: NotificationConstants.TOO_MANY_COLORS,
              categoryId: style && style.primary_category_id,
              colorLimit: style && style.color_limit
            })
          );
        } else if (tooManySleeveColors) {
          navigate(lastPath);
          dispatch(
            NotificationActions.error({
              key: NotificationConstants.TOO_MANY_SLEEVE_COLORS,
              colorLimit: SLEEVE_COLOR_LIMIT
            })
          );
        }
        return !tooManyColors;
      }
    },
    [dispatch, navigate, style, tooManyColors, tooManySleeveColors]
  );

  const checkIncompatibleNamesAndNumbers = useCallback(
    lastPath => {
      if (hasNamesAndNumbers && !productAllowsNamesAndNumbers) {
        navigate(lastPath);
        dispatch(
          NotificationActions.error({
            key: NotificationConstants.INCOMPATIBLE_NAMES_AND_NUMBERS
          })
        );
      }
    },
    [dispatch, hasNamesAndNumbers, navigate, productAllowsNamesAndNumbers]
  );

  const incompatibleSwapWarning = useCallback(
    queryParams => {
      navigate('/');

      dispatch(
        NotificationActions.error({
          key: NotificationConstants.SWAP_INCOMPATIBLE_PRODUCTS_WARNING,
          selectedProductData: {
            styleId: queryParams.styleId,
            colorId: queryParams.colorId,
            categoryPath: queryParams.categoryPath
          }
        })
      );
    },
    [dispatch, navigate]
  );

  const checkNamesAndNumbers = useCallback(
    lastPath => {
      if (hasNamesAndNumbers) {
        navigate(lastPath);
        dispatch(
          NotificationActions.error({
            key: NotificationConstants.MULTI_STYLE_NAMES_AND_NUMBERS,
            mode: 'addingStyles'
          })
        );
      }
      return hasNamesAndNumbers;
    },
    [dispatch, hasNamesAndNumbers, navigate]
  );

  const confEnterHandler = useCallback(() => {
    if (!compositeId) {
      navigate('/');
    }
  }, [compositeId, navigate]);

  const saveFormIfDesignDirty = useCallback(
    (destination, route = '/', notify = true) => {
      if (
        !compositeId ||
        canvasDirty ||
        productDirty ||
        email === DESIGN_IDEAS_EMAIL
      ) {
        notify &&
          dispatch(
            NotificationActions.error({
              title: 'We’ll need to save your design first.'
            })
          );
        dispatch(PathActions.setAfterSavePath(destination));
        navigate(route);
      }
    },
    [canvasDirty, compositeId, dispatch, email, navigate, productDirty]
  );

  const checkProductCount = useCallback(
    (lastPath, context = 'addItem') => {
      let tooManyProducts = isAtProductLimit;
      // FundraisingMode don't prevent from continuing to Goals card
      // when selection size is exactly at limit
      if (context === 'fundraisingGoals') {
        const size = productSelections.size;
        if (size <= FUNDRAISING_MAXIMUM_PRODUCT_COUNT) {
          tooManyProducts = false;
        }
      }
      if (tooManyProducts) {
        navigate(lastPath);
        dispatch(
          NotificationActions.error({
            key: NotificationConstants.TOO_MANY_PRODUCTS,
            colorLimit: SLEEVE_COLOR_LIMIT
          })
        );
      }
      return tooManyProducts;
    },
    [dispatch, isAtProductLimit, navigate, productSelections.size]
  );

  const checkNamesAndNumbersForFundraising = useCallback(
    prevPath => {
      if (hasNamesAndNumbers) {
        setTimeout(() => navigate(prevPath), 0);
        dispatch(
          NotificationActions.error({
            key: NotificationConstants.FUNDRAISING_LOAD_ERROR,
            errorText: NAMES_NUMBERS_ERROR_MESSAGE
          })
        );
      }
      return hasNamesAndNumbers;
    },
    [dispatch, hasNamesAndNumbers, navigate]
  );

  const campaignGoalsCardHandler = useCallback(() => {
    if (checkProductCount('/', 'fundraisingGoals')) {
      return;
    }
    if (checkNamesAndNumbersForFundraising('/')) {
      return;
    }
    dispatch(AnalyticsActions.fundraisingModeGoals());
  }, [checkNamesAndNumbersForFundraising, checkProductCount, dispatch]);

  const checkCanAddItems = useCallback(() => {
    if (checkProductCount(lastPath)) return false;
    if (checkNamesAndNumbers(lastPath)) return false;
    return true;
  }, [checkNamesAndNumbers, checkProductCount, lastPath]);

  const addItemsIndexHandler = useCallback(() => {
    const goToAddItems = () => navigate('/addItems/product');

    if (/addItems/.test(lastPath)) {
      navigate(-1); // keep going back if coming from sub paths
    } else if (isAnchorComplementsLoaded) {
      goToAddItems();
    } else {
      dispatch(loadComplements(anchorStyleId)).then(goToAddItems);
    }
  }, [anchorStyleId, dispatch, isAnchorComplementsLoaded, lastPath, navigate]);

  const addItemsColorHandler = useCallback(() => {
    if (checkProductCount(lastPath)) return;
    checkNamesAndNumbers(lastPath);
  }, [checkNamesAndNumbers, checkProductCount, lastPath]);

  const addItemsColorSelectedHandler = useCallback(() => {
    if (checkProductCount(lastPath)) return;
    checkNamesAndNumbers(lastPath);
  }, [checkNamesAndNumbers, checkProductCount, lastPath]);

  const addItemsProductHandler = useCallback(() => {
    dispatch(AnalyticsActions.addItems());

    if (checkProductCount(lastPath)) return;
    checkNamesAndNumbers(lastPath);
  }, [checkNamesAndNumbers, checkProductCount, dispatch, lastPath]);

  const changeProductHandler = useCallback(() => {
    navigate('/productCatalog');
  }, [navigate]);

  const selectIncompatibleProductHandler = useCallback(() => {
    if (incompatibleSwapWarning(queryParams)) {
      navigate('/');
    }
    // TODO: check if we need location.pathname
  }, [incompatibleSwapWarning, navigate, queryParams, location.pathname]);

  const artToolsHandler = useCallback(() => {
    dispatch(AnalyticsActions.artTools());
  }, [dispatch]);

  const nextHandler = useCallback(() => {
    dispatch(
      AnalyticsActions.next({
        lastPath,
        hasQuote: hasQuote && hasTouchedQuantities
      })
    );
    checkBlankDesign(lastPath);
    checkColorCount(lastPath);
    checkIncompatibleNamesAndNumbers(lastPath);
  }, [
    checkBlankDesign,
    checkColorCount,
    checkIncompatibleNamesAndNumbers,
    dispatch,
    hasQuote,
    hasTouchedQuantities,
    lastPath
  ]);

  const emailConfirmationHandler = useCallback(() => {
    confEnterHandler();
  }, [confEnterHandler]);

  const gofHandler = useCallback(async () => {
    let url;

    if (!compositeId) {
      url = '/groups';
    } else {
      url = `/g/${compositeId}/setup/start?refsrc=ndx`;
      if (lastPath === '/saveForm') {
        url = `${url}&ndx_save=true`;
      }
      try {
        projectClient.project.items = [];
        await projectClient.save();
        await projectClient.addItems([
          {
            design: {cid: compositeId},
            product: {
              styleId: Number(productSelections.getIn([0, 'styleId'])),
              colorId: Number(productSelections.getIn([0, 'colorId']))
            }
          }
        ]);
        await projectClient.convert();
      } catch (error) {
        rollbar.error('Failed to set up GOF via Project Service', error);
      }
    }
    window.location.href = url;
  }, [compositeId, lastPath, productSelections]);

  const saveContinueHandler = useCallback(() => {
    dispatch(
      AnalyticsActions.saveForm({
        path: location.pathname,
        queryParams
      })
    );
  }, [dispatch, location.pathname, queryParams]);

  const saveContinueLeaveHandler = useCallback(() => {
    dispatch(PathActions.resetAfterSavePath());
  }, [dispatch]);

  const verifyHandler = useCallback(() => {
    dispatch(
      AnalyticsActions.saveForm({
        path: location.pathname,
        queryParams
      })
    );
    dispatch(PathActions.setAfterSavePath('/next/checkout'));
  }, [dispatch, location.pathname, queryParams]);

  const checkoutHandler = useCallback(() => {
    if (
      !matchPath('/next/', lastPath) &&
      !matchPath('/next/verify/', lastPath) &&
      !matchPath('/next/verifyIntl/', lastPath)
    ) {
      navigate('/');
    } else {
      if (matchPath('/next/verify/', lastPath)) {
        const checkoutSummaryUrl = `${remoteServices.checkout}/redirect?cid=${compositeId}`;
        window.location.href = checkoutSummaryUrl;
      }
    }
  }, [compositeId, lastPath, navigate]);

  const verifyIntlHandler = useCallback(() => {
    metrics.pageViewTag('ndx/intl/verify/form');
  }, []);

  const shareEmailHandler = useCallback(() => {
    saveFormIfDesignDirty(location.pathname, '/next/saveForm', false);
  }, [location.pathname, saveFormIfDesignDirty]);

  const summaryHandler = useCallback(() => {
    if (checkColorCount(lastPath) && checkBlankDesign(lastPath)) {
      navigate('/next/summary');
    }
  }, [checkBlankDesign, checkColorCount, lastPath, navigate]);

  const intlQuoteHandler = useCallback(
    setNavigateTo => {
      if (matchPath('/next/quote', lastPath)) {
        setNavigateTo?.('/next/intlQuote?src=quote');
      } else if (matchPath('/next/buy', lastPath)) {
        setNavigateTo?.('/next/intlQuote?src=buy');
      }
    },
    [lastPath]
  );

  const intlQuoteFireSrcHandler = useCallback(() => {
    if (queryParams['src'] === 'buy') {
      metrics.pageViewTag('ndx/buy/price/international');
    } else {
      metrics.pageViewTag('ndx/quote/price/international');
    }
  }, [queryParams]);

  const tipsHandler = useCallback(() => {
    if (
      !matchPath('/next/tips', location.pathname) &&
      checkColorCount(lastPath) &&
      checkBlankDesign(lastPath)
    ) {
      navigate('/next/tips');
    }
  }, [
    checkBlankDesign,
    checkColorCount,
    lastPath,
    location.pathname,
    navigate
  ]);

  const onLeaveAddItemsColorHandler = useCallback(() => {
    dispatch(AnalyticsActions.addItemsColorClose());
  }, [dispatch]);

  const quoteHandler = useCallback(() => {
    if (matchPath('/next/quote', location.pathname)) {
      dispatch(AnalyticsActions.quoteForm());
    }
    if (matchPath('/next/verify/', lastPath)) {
      navigate('/next/buy');
    }
  }, [dispatch, lastPath, location.pathname, navigate]);

  const buyHandler = useCallback(() => {
    dispatch(AnalyticsActions.buyForm());
  }, [dispatch]);

  const saveNotesConfirmationHandler = useCallback(() => {
    dispatch(PathActions.setAfterSavePath('/notes/confirmed'));
    checkColorCount(lastPath, true);
  }, [checkColorCount, dispatch, lastPath]);

  const notesHandler = useCallback(() => {
    dispatch(AnalyticsActions.notes());
  }, [dispatch]);

  const productColorLeaveHandler = useCallback(() => {
    dispatch(AnalyticsActions.productColor('close'));
  }, [dispatch]);

  const productCatalogHandler = useCallback(() => {
    dispatch(AnalyticsActions.productCatalog({queryParams}));
    if (
      location.pathname.indexOf('productCatalogOrderMore') > -1 &&
      checkProductCount(lastPath)
    ) {
      return;
    }
  }, [checkProductCount, dispatch, lastPath, location.pathname, queryParams]);

  const productCatalogLeaveHandler = useCallback(() => {
    dispatch(AnalyticsActions.productCatalog('close'));
  }, [dispatch]);

  const checkMultiStyleNamesAndNumbers = useCallback(() => {
    if (hasMultiItem) {
      navigate(-1);
      dispatch(
        NotificationActions.error({
          key: NotificationConstants.MULTI_STYLE_NAMES_AND_NUMBERS,
          mode: 'addingNames'
        })
      );
    }
    return hasMultiItem;
  }, [dispatch, hasMultiItem, navigate]);

  const namesAndNumbersHandler = useCallback(() => {
    dispatch(AnalyticsActions.namesAndNumbers());
    checkMultiStyleNamesAndNumbers();
  }, [checkMultiStyleNamesAndNumbers, dispatch]);

  const fireNamesAndNumbersCloseAnalytics = useCallback(() => {
    dispatch(AnalyticsActions.namesAndNumbersClose());
  }, [dispatch]);

  const namesAndNumbersLeaveHandler = useCallback(() => {
    fireNamesAndNumbersCloseAnalytics();
  }, [fireNamesAndNumbersCloseAnalytics]);

  const namesAndNumbersToolsHandler = useCallback(() => {
    const icon = namesIcon || numbersIcon;
    if (icon) {
      if (selectedViewId !== icon.viewId) {
        dispatch(CanvasActions.changeView(icon.viewId));
      }
      dispatch(selectIcon({iconId: icon.id}));
    }
  }, [dispatch, namesIcon, numbersIcon, selectedViewId]);

  const saveFormHandler = useCallback(() => {
    const path = location.pathname;
    dispatch(AnalyticsActions.saveForm({path, queryParams}));
    if (path.indexOf('namesAndNumbers') > -1) {
      dispatch(
        PathActions.setAfterSavePath('/namesAndNumbers/saveConfirmation')
      );
    }
    checkColorCount(lastPath, true);
  }, [checkColorCount, dispatch, lastPath, location.pathname, queryParams]);

  const saveFormLeaveHandler = useCallback(() => {
    dispatch(PathActions.resetAfterSavePath());
  }, [dispatch]);

  const helpRouteHandler = useCallback(() => {
    dispatch(AnalyticsActions.helpMenu({queryParams}));

    switch (queryParams.action) {
      case 'call':
        window.location.href = SUPPORT_PHONE_NUMBER.TEL_LINK;
        break;
      case 'email':
        window.location.href = SUPPORT_MAILTO_ADDRESS;
        break;
      case 'feedback':
        window.location.href = FEEDBACK_MAILTO_ADDRESS;
        break;
      default:
        break;
    }
  }, [dispatch, queryParams]);

  const shoppingCartConfirmHandler = useCallback(
    mode => {
      const promptValue = (mode => {
        switch (mode) {
          case 'dirty':
            return NotificationConstants.SHOPPING_CART_CONFIRM_DIRTY;
          case 'empty':
            return NotificationConstants.SHOPPING_CART_CONFIRM_EMPTY;
          default:
            return NotificationConstants.SHOPPING_CART_CONFIRM;
        }
      })(mode);
      dispatch(
        NotificationActions.prompt({
          key: promptValue,
          mode: promptValue
        })
      );
    },
    [dispatch]
  );

  const creativeServicesHandler = () => {
    window.location.href = window.location.origin + '/ink/customcreations';
  };

  const fundraisingHandler = useCallback(() => {
    window.location.href = compositeId
      ? `${remoteServices.fundraising_goals}?cid=${compositeId}&ref=CI_NDX&utm_campaign=repeat-saver&utm_medium=customink_promo&utm_source=ndx`
      : `${remoteServices.fundraising}`;
  }, [compositeId]);

  const getPriceHandler = useCallback(() => {
    if (checkColorCount(lastPath) && checkBlankDesign(lastPath)) {
      navigate('/next/quote', {replace: true});
    }
    if (lastPath === '/next/verify/') {
      navigate('/next/buy', {replace: true});
    }
  }, [checkBlankDesign, checkColorCount, lastPath, navigate]);

  const homeHandler = useCallback(() => {
    dispatch(setRestingScreenState({icons: iconsArray}));
    triggerPageViewAnalyticsTag(lastPath);
    if (matchPath('/next/', lastPath)) {
      dispatch(PathActions.setOrderOptionsPath('/next'));
    }
  }, [dispatch, iconsArray, lastPath]);

  return {
    addItemsIndexHandler,
    addItemsProductHandler,
    addItemsColorSelectedHandler,
    addItemsColorHandler,
    checkCanAddItems,
    changeProductHandler,
    creativeServicesHandler,
    onLeaveAddItemsColorHandler,
    artToolsHandler,
    nextHandler,
    quoteHandler,
    emailConfirmationHandler,
    fundraisingHandler,
    getPriceHandler,
    homeHandler,
    saveContinueHandler,
    saveContinueLeaveHandler,
    shoppingCartConfirmHandler,
    verifyHandler,
    checkoutHandler,
    verifyIntlHandler,
    shareEmailHandler,
    summaryHandler,
    intlQuoteHandler,
    intlQuoteFireSrcHandler,
    tipsHandler,
    gofHandler,
    buyHandler,
    saveNotesConfirmationHandler,
    notesHandler,
    productColorLeaveHandler,
    productCatalogHandler,
    productCatalogLeaveHandler,
    namesAndNumbersHandler,
    namesAndNumbersLeaveHandler,
    namesAndNumbersToolsHandler,
    saveFormHandler,
    saveFormLeaveHandler,
    campaignGoalsCardHandler,
    helpRouteHandler,
    selectIncompatibleProductHandler
  };
};

export default useRouteHandlers;
